import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FirearmTypeClient, FirearmTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getFirearmTypeClient = () => new FirearmTypeClient(API_BASE_URL, axiosInstance);

// TanStack Query Hooks

// Cached lookup for dropdowns
export const useFirearmTypes = () =>
  useQuery({
    queryKey: ['firearmTypes'],
    queryFn: async () => {
      const response = await getFirearmTypeClient().searchFirearmTypes({
        filters: { isActive: true },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Non-cached search for admin tables
export const useFirearmTypeSearch = () => {
  const search = async (params: any) => {
    const response = await getFirearmTypeClient().searchFirearmTypes(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get by ID
export const useFirearmTypeById = (id: string) =>
  useQuery({
    queryKey: ['firearmType', id],
    queryFn: async () => {
      const response = await getFirearmTypeClient().getFirearmTypeById(id);
      return response?.data;
    },
    enabled: !!id,
  });

// Create mutation
export const useCreateFirearmType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FirearmTypeDTO) => {
      await getFirearmTypeClient().createFirearmType(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmTypes'] });
    },
  });
};

// Update mutation
export const useUpdateFirearmType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FirearmTypeDTO }) => {
      await getFirearmTypeClient().updateFirearmType(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmTypes'] });
    },
  });
};

// Delete mutation
export const useDeleteFirearmType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getFirearmTypeClient().deleteFirearmType(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmTypes'] });
    },
  });
};